// @flow
import React from "react"
import moment from "moment"
import { values, isEmpty, path, clone } from "ramda"
import {
  compose,
  withStateHandlers,
  lifecycle,
  type HOC,
} from "recompose"
import withQuery from "hoc/withQuery"
import withConnect from "hoc/withConnect"
import { productAvailabilityQuery } from "data/product/graphql/queries"
import type { ProductVariantAvailableAffiliate } from "data/product/types"
import type { DateRangeValue, DateRange } from "data/units/date/types"
import { dateStringFromMoment, fromDateString } from "data/units/date/helpers"
import type { ID } from "data/enums/types"
import { dayStatus } from "data/reservations/constants"
import { setAvailability } from "data/reservations/actions"
import { selectAvailability } from "data/reservations/selectors"
import Help from "./Help"
import Month from "./Month"
import PrevNextArrows from "./PrevNextArrows"
import { CalendarWrap, MonthsWrap } from "./styled"
import { getCurrentMonth, getNextMonth } from "./helpers"
import ErrorMessage, { type Error as ErrorType } from "./ErrorMessage"

const Calendar = ({
  availability,
  closeModal,
  onChange,
  onError,
  errorMessage,
  value,
  data,
  offset,
  prev,
  next,
  variantAffiliate,
  originalReservation,
  clear,
}) => {
  const currentDate = data && data.affiliateCurrentDate
    ? moment(data.affiliateCurrentDate)
    : moment()

  const availabilityDates = availability.length
    ? availability.map(dateRange => ({
      startDate: dateRange.startDate && moment(dateRange.startDate),
      endDate: dateRange.endDate && moment(dateRange.endDate),
    }))
    : availability

  const closedDays = data && data.closedDays

  const originalReservationDates = originalReservation && {
    startDate: originalReservation.startDate && moment(originalReservation.startDate),
    endDate: originalReservation.endDate && moment(originalReservation.endDate),
  }

  const valueMoment = {
    startDate: value && value.startDate && moment(value.startDate),
    endDate: value && value.endDate && moment(value.endDate),
  }

  const month = getCurrentMonth(offset)
  const nextMonth = getNextMonth(offset)

  return (
    <CalendarWrap>
      <Help value={value} originalReservation={originalReservation} />
      {errorMessage && (
        <ErrorMessage error={errorMessage} closeModal={closeModal} />
      )}
      <PrevNextArrows prev={prev} next={next} offset={offset} />
      <MonthsWrap>
        <Month
          availabilityDates={availabilityDates}
          value={valueMoment}
          closedDays={closedDays}
          onError={onError}
          onChange={onChange}
          variantAffiliate={variantAffiliate}
          month={month}
          originalReservation={originalReservationDates}
          currentDate={currentDate}
          clear={clear}
        />
        <Month
          availabilityDates={availabilityDates}
          value={valueMoment}
          closedDays={closedDays}
          onError={onError}
          onChange={onChange}
          variantAffiliate={variantAffiliate}
          month={nextMonth}
          originalReservation={originalReservationDates}
          currentDate={currentDate}
          clear={clear}
        />
      </MonthsWrap>
    </CalendarWrap>
  )
}

type Outter = {|
  closeModal: () => void,
  value?: DateRangeValue,
  onError?: ErrorType => mixed,
  errorMessage?: ErrorType,
  onChange: (?DateRangeValue) => mixed,
  variantAffiliate?: ProductVariantAvailableAffiliate,
  availabilityVariables?: {|
    productVariantId: ID,
    affiliateId: ID,
  |},
  originalReservation?: DateRange,
  clear: Function,
|}

const mapStateToProps = state => ({
  availability: selectAvailability(state),
})

const enhancer: HOC<*, Outter> = compose(
  withConnect(mapStateToProps, { setAvailability }),
  withStateHandlers(
    {
      offset: 0,
    },
    {
      next: ({ offset }) => () => ({ offset: offset + 1 }),
      prev: ({ offset }) => () => ({ offset: offset - 1 }),
      setOffset: ({ offset }) => v => ({ offset: v }),
    },
  ),
  withQuery(productAvailabilityQuery, {
    variables: props => {
      if (!props.availabilityVariables) {
        throw new Error("impossible due to skip")
      }
      const dateRange = {
        start: dateStringFromMoment(
          moment()
            .add(props.offset, "months")
            .startOf("month"),
        ),
        end: dateStringFromMoment(
          moment()
            .add(props.offset + 1, "months")
            .endOf("month"),
        ),
      }

      const affiliateId = parseInt(path(["availabilityVariables", "affiliateId"], props), 10)
      return {
        availabilityFilter: {
          ...props.availabilityVariables,
          ...dateRange,
        },
        closedDaysFilter: {
          affiliateId,
          ...dateRange,
        },
        affiliateId,
      }
    },
    config: {
      skip: props => !props.availabilityVariables,
    },
    noEmpty: true,
    noLoader: true,
  }),

  lifecycle({
    componentDidMount() {
      const { value, setOffset } = this.props

      if (path(["endDate"], value)) {
        setOffset(moment.utc(value.endDate).diff(moment.utc(), "months", false))
      }
    },
    componentDidUpdate(prevProps) {
      const {
        data,
        value,
        onChange,
        onError,
        setAvailability,
        loading,
        availability,
      } = this.props

      // For tracking availability in Redux
      if (data) {
        let isEqual = data.availability && JSON.stringify(availability) === JSON.stringify(data.availability)
        if (!loading && !isEqual) {
          let currentAvailability = availability || []
          const newAvailability = data.availability

          const currentAvailabilityIsEmpty = isEmpty(currentAvailability)
          const newAvailabilityIsEmpty = isEmpty(newAvailability)
          if (currentAvailabilityIsEmpty) {
            if (!newAvailabilityIsEmpty) {
              setAvailability(newAvailability)
            }
          } else {
            if (!newAvailabilityIsEmpty) {
              currentAvailability = clone(currentAvailability)
              const currentLastRange = currentAvailability[currentAvailability.length - 1]

              newAvailability.forEach(range => {
                const startDate = moment(fromDateString(range.startDate))
                const endDate = moment(fromDateString(range.endDate))
                // merge consecutive overlaping availability date ranges
                if (startDate.isBetween(
                  fromDateString(currentLastRange.startDate),
                  fromDateString(currentLastRange.endDate),
                  "day",
                  "[]",
                )) {
                  if (endDate.isAfter(fromDateString(currentLastRange.endDate))) {
                    // expand current last range end date
                    currentLastRange.endDate = range.endDate
                  }
                  // else don't process redundant sub-range
                } else if (startDate.isAfter(currentLastRange.endDate)) {
                  // concat if no overlap
                  currentAvailability.push(range)
                }
              })
              // only update if availability changed, avoid infinite loops
              isEqual = JSON.stringify(availability) === JSON.stringify(currentAvailability)
              !isEqual && setAvailability(currentAvailability)
            }
          }
        }
      }

      // Closed day selected in Calendar Filter
      const closedDays = data && data.closedDays

      if (value && closedDays) {
        const dates = values(value)

        const fallsOnClosedDay = closedDays.some(closedDay =>
          dates.includes(closedDay),
        )

        if (fallsOnClosedDay) {
          onChange({
            startDate: undefined,
            endDate: undefined,
          })
          onError(dayStatus.closedSelected)
        }
      }
    },
  }),
)

export default enhancer(Calendar)



// WEBPACK FOOTER //
// ./src/components/Calendar/index.js